import { isPackageAttribute } from "@/lib/products/package-attribute";
import { parseSku } from "@/lib/stock-sync/sku-parser";
export type PackVariant = {
	id: string;
	productId: string;
	currency: string;
	price: number;
	packSize: number;
	attributes: Record<string, readonly string[]>;
	availableQuantity: number | null;
};
export type PackUpgradeOffer = {
	sourceVariantId: string;
	targetVariantId: string;
	quantity: number;
	sourcePackSize: number;
	targetPackSize: number;
	targetPackPrice: number;
	targetUnitPrice: number;
	merchandiseDelta: number;
	savingsVsSingles: number | null;
	currency: string;
};
function identity(v: PackVariant, packAttributeSlug: string) {
	return JSON.stringify(
		Object.entries(v.attributes)
			.filter(([key]) => key !== packAttributeSlug)
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([key, values]) => [key, [...new Set(values)].sort()]),
	);
}
function valid(v: PackVariant) {
	return (
		Boolean(v.currency) &&
		Number.isFinite(v.price) &&
		v.price >= 0 &&
		Number.isSafeInteger(v.packSize) &&
		v.packSize > 0
	);
}
export function findPackUpgrade(
	current: PackVariant,
	quantity: number,
	variants: readonly PackVariant[],
	packAttributeSlug: string,
): PackUpgradeOffer | null {
	if (!valid(current) || !Number.isSafeInteger(quantity) || quantity < 1) return null;
	const same = variants.filter(
		(v) =>
			valid(v) &&
			v.productId === current.productId &&
			v.currency === current.currency &&
			identity(v, packAttributeSlug) === identity(current, packAttributeSlug),
	);
	const larger = same
		.filter((v) => v.id !== current.id && v.packSize > current.packSize)
		.sort((a, b) => a.packSize - b.packSize);
	const target = larger[0];
	if (
		!target ||
		larger.filter((v) => v.packSize === target.packSize).length !== 1 ||
		(target.availableQuantity !== null && target.availableQuantity < quantity)
	)
		return null;
	const singles = same.filter((v) => v.packSize === 1);
	return {
		sourceVariantId: current.id,
		targetVariantId: target.id,
		quantity,
		sourcePackSize: current.packSize,
		targetPackSize: target.packSize,
		targetPackPrice: target.price,
		targetUnitPrice: target.price / target.packSize,
		merchandiseDelta: quantity * (target.price - current.price),
		savingsVsSingles:
			singles.length === 1
				? Math.max(0, quantity * (singles[0].price * target.packSize - target.price))
				: null,
		currency: target.currency,
	};
}
export type PackCatalogVariant = {
	id: string;
	sku?: string | null;
	quantityAvailable?: number | null;
	selectionAttributes?: readonly {
		attribute: { id?: string; slug?: string | null };
		values: readonly { id?: string; slug?: string | null; value?: string | null }[];
	}[];
	pricing?: { price?: { gross: { amount: number; currency: string } } | null } | null;
};
/** Reuse the inventory system's maintained SKU family. A real single SKU in this product is required. */
export function toPackVariants(productId: string, variants: readonly PackCatalogVariant[]): PackVariant[] {
	const result: PackVariant[] = [];
	for (const variant of variants) {
		const parsed = parseSku(variant.sku ?? "");
		const money = variant.pricing?.price?.gross;
		if (!parsed || !money || !variants.some((v) => v.sku === parsed.baseSku)) continue;
		const attributes: Record<string, readonly string[]> = {
			sku_family: [parsed.baseSku],
			pack: [String(parsed.packCount)],
		};
		let safe = true;
		for (const attr of variant.selectionAttributes ?? []) {
			if (isPackageAttribute(attr.attribute.slug ?? "")) continue;
			const key = attr.attribute.id ?? attr.attribute.slug;
			const values = attr.values.map((v) => v.id ?? v.slug ?? v.value).filter((v): v is string => Boolean(v));
			if (!key || values.length !== attr.values.length) {
				safe = false;
				break;
			}
			attributes[key] = values;
		}
		if (safe)
			result.push({
				id: variant.id,
				productId,
				currency: money.currency,
				price: money.amount,
				packSize: parsed.packCount,
				attributes,
				availableQuantity: variant.quantityAvailable ?? null,
			});
	}
	return result;
}
